import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { apiList, apiPost } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { NotificationRow } from '@/lib/types';
import { NotificationDialog } from './notification-dialog';

type NotificationPayload = { items: NotificationRow[]; unread: number };

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiList<NotificationPayload>('/notifications', { params: { limit: 10 } }),
    // Polling is enough at this size; the websocket channel lands with the
    // modules that actually generate live events.
    refetchInterval: 60_000,
  });

  const markAllRead = useMutation({
    mutationFn: () => apiPost('/notifications/read-all'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiPost('/notifications/read', { ids: [id] }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /** The notification whose popup is open. */
  const [opened, setOpened] = useState<NotificationRow | null>(null);

  const items = data?.data.items ?? [];
  const unread = data?.data.unread ?? 0;

  // Opening it is reading it. The list closes so the popup is the only thing
  // on screen, the way a phone opens a notification into its app.
  const openNotification = (item: NotificationRow) => {
    setOpen(false);
    setOpened(item);
    if (!item.readAt) markRead.mutate(item.id);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        className="relative rounded-md p-2 transition-colors hover:bg-accent"
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-50 mt-1 w-80 rounded-md border bg-popover shadow-lg">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-medium">Notifications</span>
              {unread > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="scrollbar-thin max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                  You are all caught up.
                </p>
              ) : (
                items.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => openNotification(item)}
                    className={cn(
                      'block w-full border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted',
                      !item.readAt && 'bg-primary/5',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!item.readAt && (
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{item.title}</p>
                        {item.body && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{item.body}</p>
                        )}
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {timeAgo(item.createdAt)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="border-t px-3 py-2">
              <Link
                to="/account?tab=notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                Notification settings
              </Link>
            </div>
          </div>
        </>
      )}

      {opened && <NotificationDialog notification={opened} onClose={() => setOpened(null)} />}
    </div>
  );
}
